import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import UploadImage from "../../components/UploadImage";
import "../../styles/Edit.css";
import backendUrl from "../../BackendUrl";

const EditAccessory = () => {
  const { id } = useParams(); // accessory ID from URL
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("adminToken");

  const [accessoryData, setAccessoryData] = useState({
    vehicleClassId: "",
    accessory: "",
    description: "",
    price: 0,
    accessoryImg: "",
    accessoryImgId: "",
    trimIds: [],
    mpgCity: 0,
    mpgHwy: 0,
    mpgCmb: 0,
    mileage: 0,
    weight: 0,
    seats: 0,
    engine: "",
    transmission: "",
  });
  const [trimOptions, setTrimOptions] = useState([]);
  const [models, setModels] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch accessory data
  useEffect(() => {
    const fetchAccessoryData = async () => {
      try {
        const res = await axios.get(
          `${backendUrl}/api/vehicles/get-accessory/${id}`
        );
        setAccessoryData((prev) => ({
          ...prev,
          ...res.data,
        }));
      } catch (err) {
        setError("Failed to fetch accessory data.");
      } finally {
        setLoading(false);
      }
    };
    fetchAccessoryData();
  }, [id]);

  // Fetch models & trims based on vehicleClassId
  useEffect(() => {
    if (!accessoryData.vehicleClassId) return;

    const fetchModelsAndTrims = async () => {
      try {
        // 1. Models
        const resModels = await axios.get(
          `${backendUrl}/api/vehicles/get-model`,
          { params: { vehicleClassId: accessoryData.vehicleClassId } }
        );
        setModels(resModels.data);

        // 2. Trims
        const modelIds = resModels.data.map((m) => m._id);
        const resTrims = await axios.get(
          `${backendUrl}/api/vehicles/get-trim`,
          { params: { vehicleModelId: modelIds } }
        );
        setTrimOptions(resTrims.data);
      } catch (err) {
        console.error("Failed to fetch models or trims.", err);
      }
    };
    fetchModelsAndTrims();
  }, [accessoryData.vehicleClassId]);

  // Generic handler for text/number inputs
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setAccessoryData((prev) => ({
      ...prev,
      [name]:
        type === "number" ||
        ["price", "mpgCity", "mpgHwy", "mpgCmb", "mileage", "weight", "seats"].includes(
          name
        )
          ? Number(value)
          : value,
    }));
  };

  // Submit updated accessory
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${backendUrl}/api/vehicles/update-accessory/${id}`,
        accessoryData,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      setAccessoryData(response.data);
      alert("Accessory updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update accessory. Please try again later.");
    }
  };

  // Delete accessory
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this accessory?")) return;
    try {
      await axios.delete(
        `${backendUrl}/api/vehicles/delete-accessory/${id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      alert("Accessory deleted successfully.");
      navigate("/edit-website");
    } catch (err) {
      console.error(err);
      alert("Failed to delete the accessory. Please try again later.");
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="edit-class-container">
      {/* ─── Left: Edit Form ─── */}
      <div className="edit-form">
        <h2>Edit Accessory</h2>
        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <label>
            Accessory Name:
            <input
              type="text"
              name="accessory"
              value={accessoryData.accessory}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Description:
            <textarea
              name="description"
              rows={4}
              value={accessoryData.description}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Price:
            <input
              type="number"
              name="price"
              value={accessoryData.price}
              onChange={handleInputChange}
            />
          </label>

          {/* Performance Specs */}
          <h3>Performance Specs</h3>
          <label>
            MPG (City):
            <input
              type="number"
              name="mpgCity"
              value={accessoryData.mpgCity}
              onChange={handleInputChange}
            />
          </label>
          <label>
            MPG (Highway):
            <input
              type="number"
              name="mpgHwy"
              value={accessoryData.mpgHwy}
              onChange={handleInputChange}
            />
          </label>
          <label>
            MPG (Combined):
            <input
              type="number"
              name="mpgCmb"
              value={accessoryData.mpgCmb}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Mileage:
            <input
              type="number"
              name="mileage"
              value={accessoryData.mileage}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Weight:
            <input
              type="number"
              name="weight"
              value={accessoryData.weight}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Seats:
            <input
              type="number"
              name="seats"
              value={accessoryData.seats}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Engine:
            <input
              type="text"
              name="engine"
              value={accessoryData.engine}
              onChange={handleInputChange}
            />
          </label>
          <label>
            Transmission:
            <input
              type="text"
              name="transmission"
              value={accessoryData.transmission}
              onChange={handleInputChange}
            />
          </label>

          {/* Trims Selection */}
          <div className="trims-section">
            <h3>Select Trims</h3>
            {models.map((model) => {
              const modelTrims = trimOptions.filter(
                (t) => t.vehicleModelId === model._id
              );
              if (!modelTrims.length) return null;
              return (
                <div key={model._id} className="model-trim-group">
                  <h4>{model.model}</h4>
                  {modelTrims.map((trim) => {
                    const checked = accessoryData.trimIds.includes(trim._id);
                    return (
                      <label key={trim._id} className="trim-item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...accessoryData.trimIds, trim._id]
                              : accessoryData.trimIds.filter((id) => id !== trim._id);
                            setAccessoryData((prev) => ({
                              ...prev,
                              trimIds: next,
                            }));
                          }}
                        />
                        {trim.trim}
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* READY TOGGLE */}
          <label className="toggle">
            <input
              type="checkbox"
              name="ready"
              checked={accessoryData?.ready || false}
              onChange={(e) =>
                setAccessoryData((prev) => ({
                  ...prev,
                  ready: e.target.checked,
                }))
              }
            />
            <span>Ready</span>
          </label>

          <button type="submit">Save Changes</button>
        </form>

        {/* Image Upload */}
        <UploadImage
          uploadUrl="/api/vehicles/upload-accessory"
          id={id}
          onUploadSuccess={(data) => {
            setAccessoryData((prev) => ({
              ...prev,
              accessoryImg: data.url,
              accessoryImgId: data.public_id,
            }));
          }}
        />

        {/* Delete */}
        <button
          type="button"
          onClick={handleDelete}
          style={{ backgroundColor: "red", color: "white", marginTop: "1rem" }}
        >
          Delete Accessory
        </button>
      </div>

      {/* ─── Right: Display Current Accessory ─── */}
      <div className="display-section">
        {accessoryData.accessoryImg && (
          <div className="image-container">
            <img src={accessoryData.accessoryImg} alt="Accessory" />
            <div className="image-title">{accessoryData.accessory}</div>
          </div>
        )}
        <p className="description">{accessoryData.description}</p>
        <p className="price">Price: ${accessoryData.price}</p>
      </div>
    </div>
  );
};

export default EditAccessory;